import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import { collection, query, where, getDocs, orderBy, limit, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Cache constants
const QUERIES_CACHE_KEY = 'saved_queries_cache';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes in milliseconds
const QUERY_SETTINGS_KEY = 'query_visualization_settings'; // New constant for storing visualization settings

/**
 * Core hook for query library data fetching and caching
 * @returns {Object} Core functions and state for query management
 */
export const useQueryLibraryCore = () => {
  const { currentUser } = useAuth();
  const { config } = useConfig();
  
  const [queries, setQueries] = useState(() => {
    // Initialize from cache if available
    try {
      const cachedData = localStorage.getItem(QUERIES_CACHE_KEY);
      if (cachedData) {
        const { data, timestamp, userId } = JSON.parse(cachedData);
        // Accept cached data on initial load regardless of expiry
        // Will refresh in background if needed
        if (userId === currentUser?.email) {
          return data || [];
        }
      }
    } catch (error) {
    }
    return [];
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [queryJustAdded, setQueryJustAdded] = useState(false);

  // Function to read visualization settings from cache
  const getQuerySettings = useCallback(() => {
    if (!currentUser?.email) {
      return {};
    }
    
    try {
      // Create a settings object where keys are queryIds and values are the user's settings
      const settings = {};
      
      // Loop through all queries in our state
      queries.forEach(query => {
        if (query.userSettings && query.userSettings[currentUser.email]) {
          settings[query.id] = query.userSettings[currentUser.email];
        }
      });
      
      return settings;
    } catch (error) {
      return {};
    }
  }, [currentUser, queries]);

  // Function to save visualization settings to cache
  const saveQuerySettings = useCallback(async (queryId, settings) => {
    if (!queryId || !settings || !currentUser?.email) {
      return;
    }
    
    try {
      // Log before update
      
      // Reference to the query document in Firestore
      const queryRef = doc(db, 'SavedQueries', queryId);
      
      // Get the current document to check if it exists and retrieve current settings
      const queryDoc = await getDoc(queryRef);
      
      if (!queryDoc.exists()) {
        return;
      }
      
      // Get current data from the document
      const queryData = queryDoc.data();
      
      // Initialize or update the userSettings property in the document
      // This will be a map where each key is a user email and the value is their settings
      const currentUserSettings = queryData.userSettings || {};
      
      // Update with new settings for this specific user
      const updatedUserSettings = {
        ...currentUserSettings,
        [currentUser.email]: {
          // Preserve any existing settings for this user
          ...(currentUserSettings[currentUser.email] || {}),
          // Apply new settings
          ...settings,
          // Add timestamp for tracking
          _lastUpdated: new Date()
        }
      };
      
      // Prepare the update object for Firestore
      const updateObject = {
        userSettings: updatedUserSettings
      };
      
      // If there's a custom title in the settings, update it in the userCustomTitles field
      if (settings.customTitle) {
        // Get existing custom titles or initialize an empty object
        const userCustomTitles = queryData.userCustomTitles || {};
        
        // Update title for this user
        const updatedCustomTitles = {
          ...userCustomTitles,
          [currentUser.email]: settings.customTitle
        };
        
        // Add to update object
        updateObject.userCustomTitles = updatedCustomTitles;
      }
      
      // Update the document in Firestore
      await updateDoc(queryRef, updateObject);
      
      
      // Also update the local cache for immediate use
      // This prevents having to wait for the next Firestore fetch
      setQueries(prevQueries => 
        prevQueries.map(q => 
          q.id === queryId 
            ? { 
                ...q, 
                userSettings: updatedUserSettings,
                userCustomTitles: settings.customTitle ? {
                  ...(q.userCustomTitles || {}),
                  [currentUser.email]: settings.customTitle
                } : q.userCustomTitles
              } 
            : q
        )
      );
      
    } catch (error) {
    }
  }, [currentUser, db, setQueries]);

  // Function to read from cache
  const getQueriesFromCache = useCallback(() => {
    try {
      const cachedData = localStorage.getItem(QUERIES_CACHE_KEY);
      if (!cachedData) return null;
      
      const { data, timestamp, userId } = JSON.parse(cachedData);
      
      // Check if cache is valid (not expired and belongs to current user)
      const now = Date.now();
      if (now - timestamp < CACHE_EXPIRY && userId === currentUser?.email) {
        return data;
      }
      return null;
    } catch (error) {
      return null;
    }
  }, [currentUser]);

  // Function to write to cache
  const saveQueriesToCache = useCallback((data) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        userId: currentUser?.email
      };
      localStorage.setItem(QUERIES_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
    }
  }, [currentUser]);

  // Function to invalidate cache
  const invalidateCache = useCallback(() => {
    try {
      const cachedData = localStorage.getItem(QUERIES_CACHE_KEY);
      if (cachedData) {
        const cached = JSON.parse(cachedData);
        cached.timestamp = 0; // Force cache expiration
        localStorage.setItem(QUERIES_CACHE_KEY, JSON.stringify(cached));
      }
    } catch (cacheError) {
    }
  }, []);

  // Direct Firestore fetch function - updated to sort by createdAt
  const fetchQueriesFromFirestore = useCallback(async () => {
    if (!currentUser?.email) {
      console.log('[QueryLibrary] No current user email, skipping Firestore fetch');
      return [];
    }
    
    try {
      console.log('[QueryLibrary] Starting Firestore queries for user:', currentUser.email);
      
      // We'll first try to fetch using direct Firestore access for better performance
      const userQueries = [];
      
      // Get user's own queries - sort by createdAt descending
      const userQueryRef = query(
        collection(db, 'SavedQueries'),
        where('userId', '==', currentUser.email),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc') // Sort by creation date descending
      );
      
      console.log('[QueryLibrary] Executing user query...');
      const userQuerySnapshot = await getDocs(userQueryRef);
      console.log('[QueryLibrary] User query completed, docs found:', userQuerySnapshot.size);
      
      userQuerySnapshot.forEach(doc => {
        // Only add queries that aren't hidden
        const queryData = doc.data();
        if (!queryData.hidden) {
          userQueries.push({ id: doc.id, ...queryData });
        }
      });
      
      // Get shared queries if not admin
      let sharedQueries = [];
      if (!currentUser.isAdmin) {
        console.log('[QueryLibrary] Fetching shared queries (non-admin)...');
        const sharedQueryRef = query(
          collection(db, 'SavedQueries'),
          where('isPrivate', '==', false),
          where('isDeleted', '==', false),
          where('userId', '!=', currentUser.email),
          orderBy('userId'),
          orderBy('createdAt', 'desc'), // Sort by creation date descending
          limit(50) // Limit to keep response size reasonable
        );
        
        const sharedQuerySnapshot = await getDocs(sharedQueryRef);
        console.log('[QueryLibrary] Shared query completed, docs found:', sharedQuerySnapshot.size);
        
        sharedQuerySnapshot.forEach(doc => {
          const queryData = doc.data();
          // Only add shared queries that are explicitly in library and not hidden
          if (!queryData.hidden && queryData.inLibrary === true) {
            sharedQueries.push({ id: doc.id, ...queryData });
          }
        });
      } else {
        console.log('[QueryLibrary] Fetching all queries (admin)...');
        // For admins, fetch all queries (with limit)
        const allQueriesRef = query(
          collection(db, 'SavedQueries'),
          where('isDeleted', '==', false),
          orderBy('createdAt', 'desc'), // Sort by creation date descending
          limit(100) // Higher limit for admins but still reasonable
        );
        
        const allQueriesSnapshot = await getDocs(allQueriesRef);
        console.log('[QueryLibrary] Admin query completed, docs found:', allQueriesSnapshot.size);
        
        allQueriesSnapshot.forEach(doc => {
          const queryData = doc.data();
          // For admin: only filter shared queries by inLibrary, show all own queries
          if (queryData.userId !== currentUser.email && !queryData.hidden) {
            // If it's someone else's query, only show if it's in their library
            if (queryData.inLibrary === true || queryData.userId === currentUser.email) {
              sharedQueries.push({ id: doc.id, ...queryData });
            }
          }
        });
      }
      
      // Combine results, ensuring uniqueness
      const seen = new Set(userQueries.map(q => q.id));
      const allQueries = [
        ...userQueries,
        ...sharedQueries.filter(q => !seen.has(q.id))
      ];
      
      console.log('[QueryLibrary] Firestore fetch complete:', {
        userQueries: userQueries.length,
        sharedQueries: sharedQueries.length,
        totalQueries: allQueries.length
      });
      
      // Sort by createdAt (descending)
      allQueries.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 
                     (typeof a.createdAt === 'string' ? new Date(a.createdAt).getTime() : 0) || 
                     a.lastRun?.toMillis?.() || 
                     (typeof a.lastRun === 'string' ? new Date(a.lastRun).getTime() : 0) || 
                     0;
                     
        const bTime = b.createdAt?.toMillis?.() || 
                     (typeof b.createdAt === 'string' ? new Date(b.createdAt).getTime() : 0) || 
                     b.lastRun?.toMillis?.() || 
                     (typeof b.lastRun === 'string' ? new Date(b.lastRun).getTime() : 0) || 
                     0;
                     
        return bTime - aTime; // Newest first
      });
      
      return allQueries;
    } catch (error) {
      console.error('[QueryLibrary] Firestore fetch error details:', {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  }, [currentUser]);

  // Fallback to backend function if direct Firestore fails
  const fetchQueriesFromBackend = useCallback(async () => {
    if (!currentUser?.email || !config?.core?.projectId) return [];
    
    const response = await fetch(`https://us-central1-${config.core.projectId}.cloudfunctions.net/getSavedQueries`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        data: { 
          userId: currentUser.email,
          isAdmin: currentUser.isAdmin
        }
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch saved queries');
    }
    
    const result = await response.json();
    // Filter out hidden queries
    const queriesData = result.queries || result;
    return queriesData.filter(query => !query.hidden);
  }, [currentUser, config]);

  const fetchSavedQueries = useCallback(async (forceFresh = false) => {
    if (!currentUser || !config) return;
    
    console.log('[QueryLibrary] fetchSavedQueries called', { 
      forceFresh, 
      hasUser: !!currentUser?.email,
      hasConfig: !!config?.core?.projectId 
    });
    
    // Check if we should use cached data
    if (!forceFresh) {
      const cachedQueries = getQueriesFromCache();
      if (cachedQueries) {
        console.log('[QueryLibrary] Using cached queries', { count: cachedQueries.length });
        setQueries(cachedQueries);
        setIsLoading(false);
        return;
      }
    }
    
    setIsLoading(true);
    try {
      // Try direct Firestore first, fall back to backend
      let queriesData;
      try {
        console.log('[QueryLibrary] Attempting Firestore fetch...');
        queriesData = await fetchQueriesFromFirestore();
        console.log('[QueryLibrary] Firestore fetch successful', { count: queriesData.length });
      } catch (firestoreError) {
        console.error('[QueryLibrary] Firestore fetch failed:', {
          error: firestoreError.message,
          code: firestoreError.code,
          details: firestoreError
        });
        
        console.log('[QueryLibrary] Attempting backend fetch...');
        try {
          queriesData = await fetchQueriesFromBackend();
          console.log('[QueryLibrary] Backend fetch successful', { count: queriesData.length });
        } catch (backendError) {
          console.error('[QueryLibrary] Backend fetch also failed:', {
            error: backendError.message,
            details: backendError
          });
          throw backendError; // Re-throw to trigger the main catch block
        }
      }
      
      // Save to cache and state
      saveQueriesToCache(queriesData);
      setQueries(queriesData);
      setLastFetchTime(Date.now());
      setError(null);
    } catch (err) {
      console.error('[QueryLibrary] Both fetch methods failed:', err);
      setError('Failed to load saved queries');
      
      // Try to use cached data even if expired as a fallback
      const cachedQueries = getQueriesFromCache();
      console.log('[QueryLibrary] Checking for expired cache as fallback:', { 
        hasCached: !!cachedQueries,
        cachedCount: cachedQueries?.length || 0 
      });
      
      if (cachedQueries && cachedQueries.length > 0) {
        console.log('[QueryLibrary] Using expired cache as fallback');
        setQueries(cachedQueries);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    currentUser, 
    config, 
    getQueriesFromCache, 
    saveQueriesToCache, 
    fetchQueriesFromFirestore, 
    fetchQueriesFromBackend
  ]);

  // Function to hide a query (set hidden = true in Firestore)
  const hideQuery = useCallback(async (queryId) => {
    if (!queryId || !currentUser?.email) return;
    
    try {
      setIsLoading(true);
      
      // Update the document in Firestore
      const queryRef = doc(db, 'SavedQueries', queryId);
      await updateDoc(queryRef, {
        hidden: true,
        hiddenAt: new Date(),
        hiddenBy: currentUser.email
      });
      
      // Update local state by removing the query
      setQueries(prevQueries => prevQueries.filter(query => query.id !== queryId));
      
      // Invalidate cache
      invalidateCache();
      
    } catch (error) {
      setError('Failed to hide query. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, invalidateCache]);


  const addToLibrary = useCallback(async (result) => {
    if (!result || !currentUser?.email || !config?.core?.projectId) return null;
    
    try {
      // Use the query's name if it exists, otherwise generate one from the query text
      let name;
      if (result.name && typeof result.name === 'string' && result.name.trim()) {
        name = result.name.trim();
      } else {
        // Generate a name based on the first few words of the query
        const words = result.userQuery.split(' ');
        const nameWords = words.slice(0, Math.min(7, words.length));
        name = nameWords.join(' ');
        if (name.length > 40) {
          name = name.substring(0, 37) + '...';
        }
      }
      
      // Extract relevant card settings for saving with the query
      const baseSettings = {
        viewMode          : result.cardSettings?.viewMode  || 'table',
        chartType         : result.cardSettings?.chartType || 'bar',
        selectedXAxis     : result.cardSettings?.selectedXAxis,
        selectedYAxis     : result.cardSettings?.selectedYAxis,
        syncScales        : result.cardSettings?.syncScales    || false,
        sortColumn        : result.cardSettings?.sortColumn    || '',
        sortOrder         : result.cardSettings?.sortOrder     || 'asc',
        showSummary       : result.cardSettings?.showSummary   ?? true,
        activeFilters     : result.cardSettings?.activeFilters || {},
        transformationType: result.cardSettings?.transformationType || null,
        pivotConfig       : result.cardSettings?.pivotConfig        || null
      };
      
      const cardSettings = baseSettings.transformationType
        ? baseSettings
        : {
            ...baseSettings,
            visibleColumns: result.cardSettings?.visibleColumns || [],
            columnOrder   : result.cardSettings?.columnOrder   || []
          };
      
      
      // Store the current user's settings in the userSettings object
      const userSettings = {
        [currentUser.email]: {
          ...cardSettings,
          _lastUpdated: new Date()
        }
      };
      
      
      const response = await fetch(
        `https://us-central1-${config.core.projectId}.cloudfunctions.net/saveQuery`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: {
              userId: currentUser.email,
              name,
              userQuery: result.userQuery,
              sqlQuery: result.sqlQuery,
              llmModel: result.config?.usedModel || result.llmModel,
              fallbackUsed: result.config?.fallbackUsed || result.fallbackUsed,
              originalModel: result.originalModel,
              documentId: result.documentId,
              isPrivate: true,
              explanation: result.explanation,
              schema: result.originalSchema || result.schema,
              originalSchema: result.originalSchema || null,
              cardSettings,
              userSettings,
              hidden: false,
              inLibrary: true
            }
          })
      
        }
      );
  
      const responseData = await response.json();
  
      // Try different possible property names for the ID
      const queryId = responseData.queryId || responseData.id || responseData.savedQueryId;
      
      // After getting the queryId, ensure the userSettings are saved in Firestore
      if (queryId) {
        try {
          const queryRef = doc(db, 'SavedQueries', queryId);
          await updateDoc(queryRef, {
            userSettings: userSettings
          });
        } catch (userSettingsError) {
        }
      }
      
      // Invalidate cache
      invalidateCache();
      
      // Show a success indicator
      setQueryJustAdded(true);
      setTimeout(() => setQueryJustAdded(false), 3000);
      
      // Refresh the queries
      fetchSavedQueries(true);
      
      return responseData;
    } catch (error) {
      setError('Failed to add to library. Please try again.');
      throw error;
    }
  }, [config, currentUser, invalidateCache, fetchSavedQueries]);

  // Refresh data on config or currentUser change
  useEffect(() => {
    if (currentUser && config) {
      fetchSavedQueries();
    }
  }, [fetchSavedQueries, currentUser, config]);

  return {
    // State
    queries,
    isLoading,
    error,
    lastFetchTime,
    queryJustAdded,
    
    // State setters
    setQueries,
    setQueryJustAdded,
    
    // Functions
    fetchSavedQueries,
    addToLibrary,
    invalidateCache,
    getQueriesFromCache,
    saveQueriesToCache,
    hideQuery, // Export the hideQuery function
    
    // New functions for visualization settings
    getQuerySettings,
    saveQuerySettings
  };
};

export default useQueryLibraryCore;